import { Component } from 'react';
import { GeneratorLayout, genStyles } from '../converter-layout/generator-layout';

type Algo = 'SHA-1' | 'SHA-256' | 'SHA-384' | 'SHA-512';
type OutEnc = 'hex' | 'base64' | 'base64url';

interface State {
    message: string;
    secret: string;
    algorithm: Algo;
    outEnc: OutEnc;
    output: string;
    error: string | null;
    copied: boolean;
}

function bytesToHex(bytes: ArrayBuffer): string {
    return Array.from(new Uint8Array(bytes)).map(b => b.toString(16).padStart(2, '0')).join('');
}

function bytesToBase64(bytes: ArrayBuffer, urlSafe = false): string {
    const u8 = new Uint8Array(bytes);
    let bin = '';
    for (let i = 0; i < u8.length; i++) bin += String.fromCharCode(u8[i]);
    let b64 = btoa(bin);
    if (urlSafe) b64 = b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    return b64;
}

export default class HmacGeneratorComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    private debounceTimer: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        message: 'Hello, world!',
        secret: 'super-secret-key',
        algorithm: 'SHA-256',
        outEnc: 'hex',
        output: '',
        error: null,
        copied: false,
    };

    componentDidMount() {
        this.compute();
    }

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
        if (this.debounceTimer) clearTimeout(this.debounceTimer);
    }

    private scheduleCompute = () => {
        if (this.debounceTimer) clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(this.compute, 200);
    };

    compute = async () => {
        try {
            const enc = new TextEncoder();
            const key = await crypto.subtle.importKey(
                'raw', enc.encode(this.state.secret),
                { name: 'HMAC', hash: this.state.algorithm }, false, ['sign'],
            );
            const sig = await crypto.subtle.sign('HMAC', key, enc.encode(this.state.message));
            let out = '';
            if (this.state.outEnc === 'hex') out = bytesToHex(sig);
            else if (this.state.outEnc === 'base64') out = bytesToBase64(sig);
            else out = bytesToBase64(sig, true);
            this.setState({ output: out, error: null, copied: false });
        } catch (e: any) {
            this.setState({ error: e.message || 'HMAC failed', output: '' });
        }
    };

    copyOutput = () => {
        if (!this.state.output) return;
        navigator.clipboard.writeText(this.state.output).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        const { message, secret, algorithm, outEnc, output, error, copied } = this.state;

        const form = (
            <>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Message &amp; key</span></div>
                    <div className={genStyles.field}>
                        <label htmlFor="hmac-secret">Secret key</label>
                        <input id="hmac-secret" type="text" value={secret} spellCheck={false}
                            onChange={e => { this.setState({ secret: e.target.value }); this.scheduleCompute(); }} />
                    </div>
                    <div className={genStyles.field}>
                        <label htmlFor="hmac-msg">Message</label>
                        <textarea id="hmac-msg" rows={6} value={message} spellCheck={false}
                            onChange={e => { this.setState({ message: e.target.value }); this.scheduleCompute(); }} />
                    </div>
                </div>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Options</span></div>
                    <div className={genStyles.fieldRow}>
                        <div className={genStyles.field}>
                            <label htmlFor="hmac-algo">Algorithm</label>
                            <select id="hmac-algo" value={algorithm}
                                onChange={e => { this.setState({ algorithm: e.target.value as Algo }, this.compute); }}>
                                <option>SHA-1</option>
                                <option>SHA-256</option>
                                <option>SHA-384</option>
                                <option>SHA-512</option>
                            </select>
                        </div>
                        <div className={genStyles.field}>
                            <label htmlFor="hmac-enc">Output encoding</label>
                            <select id="hmac-enc" value={outEnc}
                                onChange={e => { this.setState({ outEnc: e.target.value as OutEnc }, this.compute); }}>
                                <option value="hex">Hex (lowercase)</option>
                                <option value="base64">Base64</option>
                                <option value="base64url">Base64 URL-safe</option>
                            </select>
                        </div>
                    </div>
                </div>
            </>
        );

        return (
            <GeneratorLayout
                form={form}
                previewLabel="HMAC"
                previewMeta={output ? `${output.length} chars · ${outEnc}` : undefined}
                preview={<pre className={genStyles.previewArea} aria-label="HMAC output">{output || (error ? `Error: ${error}` : 'HMAC will appear here once you enter a message and secret.')}</pre>}
                previewActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyOutput} disabled={!output}>
                        {copied ? '✓ Copied' : '📋 Copy HMAC'}
                    </button>
                }
            />
        );
    }
}
